import type {
  CollectionReference,
  DocumentData,
  PartialWithFieldValue,
} from 'firebase/firestore'
import {
  collection,
  collectionGroup,
  query,
  where,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { FirestoreUserPromotionRedemption } from './schema'
import { schema, writeSchema } from './schema'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { UserPromotionRedemption } from '../../models/UserPromotionRedemption'
import type { StreamInterface } from 'tricklejs/dist/types'
import { modelListStream } from '../../firestore-mobx/stream'
import { addDocWithError } from '../../firestore-mobx/fetch'
import { createUserPromotion } from '../UserPromotion'
import { PromotionType } from '../UserPromotion/types'
import { PromotionAppliesTo } from '../Promotion/types'
import {
  APPROVED_FREE_PROMOTION_NAME,
  N_FREE_SECTIONS_PROMOTION_ID,
} from '../../models/UserPromotion'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreUserPromotionRedemption> = {
  toFirestore: (
    user: PartialWithFieldValue<FirestoreUserPromotionRedemption>
  ) => {
    return writeSchema.partial().parse(user)
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

export const getColRef = (
  firestore: Firestore,
  userId: string,
  userPromotionId: string
): CollectionReference<FirestoreUserPromotionRedemption, DocumentData> => {
  return collection(
    firestore,
    'user_profile',
    userId,
    'user_promotion',
    userPromotionId,
    'user_promotion_redemption'
  ).withConverter(converter)
}

export const getUserPromotionRedemptionsForUserPromotion = (
  repository: FirebaseRepository,
  params: { userId: string; userPromotionId: string }
): StreamInterface<UserPromotionRedemption[]> => {
  const ref = getColRef(
    repository.firestore,
    params.userId,
    params.userPromotionId
  )

  return modelListStream(repository, ref, UserPromotionRedemption)
}

export const getUserPromotionRedemptions = (
  repository: FirebaseRepository,
  { userId }: { userId: string }
) => {
  const ref = collectionGroup(
    repository.firestore,
    'user_promotion_redemption'
  ).withConverter(converter)
  const q = query(ref, where('userId', '==', userId))
  return modelListStream(repository, q, UserPromotionRedemption)
}

// - `userId` string
// - `promotionId` string
// - `userPromotionId` string
// - `sectionId` optional string
// - `assignmentId` optional string
// - `roomId` optional string
// - `updatedAt` timestamp

export const redeemPromotions = async (
  repository: FirebaseRepository,
  params: {
    userId: string
    promotionId: string
    userPromotionId: string
    sectionId?: string
    assignmentId?: string
    roomId?: string
  }
) => {
  const ref = getColRef(
    repository.firestore,
    params.userId,
    params.userPromotionId
  )

  return addDocWithError(
    ref,
    {
      userId: params.userId,
      promotionId: params.promotionId,
      userPromotionId: params.userPromotionId,
      sectionId: params.sectionId,
      assignmentId: params.assignmentId,
      roomId: params.roomId,
      updatedAt: new Date(),
    },
    'RedeemPromotionsError'
  )
}

export const applyApprovedFreeAdminPromotion = async (
  repository: FirebaseRepository,
  {
    userId,
    sectionId,
  }: {
    sectionId: string
    userId: string
  }
) => {
  // create redemption record first (needs to exist to write redemption)
  const oneDayFromNow = new Date()
  oneDayFromNow.setDate(oneDayFromNow.getDate() + 1)

  const userPromotionId = await createUserPromotion(repository, userId, {
    promotionId: N_FREE_SECTIONS_PROMOTION_ID,
    expirationDate: oneDayFromNow,
    promotionName: APPROVED_FREE_PROMOTION_NAME,
    userId,
    promotionSource: PromotionType.admin,
    numberField: [],
    stringField: [],
    appliesTo: PromotionAppliesTo.section,
    consumableCount: 1,
  })

  // then redeem the promotion
  await redeemPromotions(repository, {
    sectionId,
    userId,
    promotionId: N_FREE_SECTIONS_PROMOTION_ID,
    userPromotionId,
  })
}
